import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Bluetooth, Plus, Loader2, RefreshCw, Search, Trash2, Pencil, X,
  Wifi, WifiOff, Battery, UserCog, Baby, MapPin, HelpCircle,
} from "lucide-react";

const STATUS_STYLE = {
  connected: { bg: "bg-emerald-500", text: "text-white", icon: Wifi, label: "Connecté" },
  lost: { bg: "bg-zinc-300", text: "text-zinc-900", icon: WifiOff, label: "Perdu" },
};

const TYPE_META = {
  driver: { label: "Chauffeur", icon: UserCog, bg: "bg-[#FFC60A]", desc: "Badge porté par un chauffeur (auto-start mission)" },
  passenger: { label: "Enfant / Passager", icon: Baby, bg: "bg-blue-100 text-blue-900", desc: "Badge enfant pour pointage embarquement" },
  station: { label: "Borne fixe", icon: MapPin, bg: "bg-emerald-100 text-emerald-900", desc: "Beacon à un arrêt-clé (dépôt, école)" },
  unassigned: { label: "Non classifié", icon: HelpCircle, bg: "bg-zinc-200", desc: "Détecté par un ping mais pas encore catégorisé" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.lost;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${s.bg} ${s.text}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

function TypePill({ type }) {
  const meta = TYPE_META[type] || TYPE_META.unassigned;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${meta.bg}`}>
      <Icon size={11} /> {meta.label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "à l'instant";
  if (d < 3600) return `${Math.floor(d / 60)} min`;
  if (d < 86400) return `${Math.floor(d / 3600)} h`;
  return `${Math.floor(d / 86400)} j`;
}

export default function BluetoothTags() {
  const [params] = useSearchParams();
  const initialStatusFilter = params.get("filter") === "disconnected" ? "lost" : "all";
  const [tags, setTags] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: dr }, { data: ch }] = await Promise.all([
        api.get("/logibus/bluetooth-tags"),
        api.get("/users?role=driver&active_only=true").catch(() => ({ data: [] })),
        api.get("/children").catch(() => ({ data: [] })),
      ]);
      setTags(t || []);
      setDrivers(dr || []);
      setChildren(ch || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tags.filter((t) => {
      if (typeFilter !== "all" && (t.tag_type || "unassigned") !== typeFilter) return false;
      if (statusFilter !== "all" && (t.status || "lost") !== statusFilter) return false;
      if (!q) return true;
      const blob = [t.name, t.identifier, t.driver?.name, t.child?.name, t.station_label]
        .filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [tags, search, typeFilter, statusFilter]);

  const counts = useMemo(() => ({
    total: tags.length,
    driver: tags.filter((t) => t.tag_type === "driver").length,
    passenger: tags.filter((t) => t.tag_type === "passenger").length,
    station: tags.filter((t) => t.tag_type === "station").length,
    unassigned: tags.filter((t) => !t.tag_type || t.tag_type === "unassigned").length,
    lost: tags.filter((t) => t.status === "lost").length,
  }), [tags]);

  const handleDelete = async (tag) => {
    if (!confirm(`Supprimer le badge « ${tag.name} » ?`)) return;
    try {
      await api.delete(`/logibus/bluetooth-tags/${tag.id}`);
      toast.success("Badge supprimé");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="bluetooth-tags-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Identification personnelle</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Tags Bluetooth</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Badges personnels (chauffeur · enfant · borne fixe). Les véhicules sont déjà identifiés par leur tracker Logitrak.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowCreate(true)} data-testid="create-tag-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800">
            <Plus size={14} /> Nouveau badge
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <KpiCard label="Total" value={counts.total} icon={Bluetooth} />
        <KpiCard label="Chauffeurs" value={counts.driver} icon={UserCog} tone="amber" />
        <KpiCard label="Enfants" value={counts.passenger} icon={Baby} tone="blue" />
        <KpiCard label="Bornes" value={counts.station} icon={MapPin} tone="emerald" />
        <KpiCard label="Perdus" value={counts.lost} icon={WifiOff} tone="zinc" />
      </div>

      <div className="border border-zinc-900">
        <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center gap-2 flex-wrap">
          <Search size={12} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer par nom, MAC, chauffeur, enfant…"
            data-testid="search-tag" className="flex-1 min-w-[200px] bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-500" />
          <div className="flex gap-1">
            {["all", "driver", "passenger", "station", "unassigned"].map((t) => (
              <button key={t} onClick={() => setTypeFilter(t)} data-testid={`filter-${t}`}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/30 ${typeFilter === t ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "text-zinc-300 hover:bg-zinc-800"}`}>
                {t === "all" ? "Tous" : TYPE_META[t]?.label || t}
              </button>
            ))}
          </div>
          <button onClick={() => setStatusFilter(statusFilter === "lost" ? "all" : "lost")} data-testid="filter-lost"
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/30 inline-flex items-center gap-1 ${statusFilter === "lost" ? "bg-red-500 text-white border-red-500" : "text-zinc-300 hover:bg-zinc-800"}`}>
            <WifiOff size={11} /> Perdus uniquement
          </button>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{filtered.length}</span>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-xs text-zinc-500 text-center font-mono">
            Aucun badge. Cliquez sur « Nouveau badge » ou laissez votre gateway BLE poster vers
            <code className="ml-1 bg-zinc-100 px-1 py-0.5">POST /api/logibus/bluetooth-tags/ping</code>.
          </div>
        )}
        <div className="divide-y divide-zinc-200">
          {filtered.map((t) => {
            const owner = t.tag_type === "driver" ? t.driver?.name
              : t.tag_type === "passenger" ? t.child?.name
              : t.tag_type === "station" ? t.station_label
              : null;
            return (
              <div key={t.id} data-testid={`tag-row-${t.id}`} className="p-3 grid grid-cols-12 gap-3 items-center hover:bg-zinc-50">
                <div className="col-span-12 sm:col-span-3 min-w-0">
                  <div className="font-display font-bold text-sm tracking-tight truncate">{t.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">{t.identifier}</div>
                </div>
                <div className="col-span-6 sm:col-span-2"><TypePill type={t.tag_type || "unassigned"} /></div>
                <div className="col-span-6 sm:col-span-3 min-w-0 text-xs">
                  {owner ? (
                    <span className="truncate font-semibold">{owner}</span>
                  ) : (
                    <span className="text-[10px] font-mono text-zinc-400">— à classifier —</span>
                  )}
                </div>
                <div className="col-span-6 sm:col-span-2"><StatusPill status={t.status} /></div>
                <div className="col-span-6 sm:col-span-2 flex justify-end gap-1 items-center">
                  {t.battery != null && (
                    <span className="text-[10px] font-mono text-zinc-600 mr-1 inline-flex items-center gap-1">
                      <Battery size={11} /> {t.battery}%
                    </span>
                  )}
                  <span className="text-[10px] font-mono text-zinc-500 mr-2">vu {relativeTime(t.last_seen)}</span>
                  <button onClick={() => setEditing(t)} data-testid={`edit-${t.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-zinc-900 hover:text-white">
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => handleDelete(t)} data-testid={`delete-${t.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-red-500 hover:text-white">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(showCreate || editing) && (
        <TagFormModal tag={editing} drivers={drivers} childrenList={children}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone = "zinc" }) {
  const toneBg = {
    emerald: "bg-emerald-50 border-emerald-700",
    blue: "bg-blue-50 border-blue-700",
    zinc: "bg-zinc-50 border-zinc-900",
    amber: "bg-[#FFF7D6] border-zinc-900",
  }[tone] || "bg-zinc-50 border-zinc-900";
  return (
    <div className={`border ${toneBg} p-3 flex items-center gap-3`} data-testid={`kpi-${label.toLowerCase()}`}>
      <Icon size={20} className="text-zinc-700 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 truncate">{label}</div>
        <div className="font-display font-black text-2xl leading-none">{value}</div>
      </div>
    </div>
  );
}

function TagFormModal({ tag, drivers, childrenList, onClose, onSaved }) {
  const isEdit = !!tag;
  const [name, setName] = useState(tag?.name || "");
  const [identifier, setIdentifier] = useState(tag?.identifier || "");
  const [tagType, setTagType] = useState(tag?.tag_type || "driver");
  const [driverId, setDriverId] = useState(tag?.driver_id || "");
  const [childId, setChildId] = useState(tag?.child_id || "");
  const [stationLabel, setStationLabel] = useState(tag?.station_label || "");
  const [battery, setBattery] = useState(tag?.battery ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        identifier: identifier.trim(),
        tag_type: tagType,
        driver_id: tagType === "driver" ? (driverId || null) : null,
        child_id: tagType === "passenger" ? (childId || null) : null,
        station_label: tagType === "station" ? (stationLabel || null) : null,
        battery: battery === "" ? null : Number(battery),
      };
      if (isEdit) {
        await api.put(`/logibus/bluetooth-tags/${tag.id}`, payload);
        toast.success("Badge mis à jour");
      } else {
        await api.post("/logibus/bluetooth-tags", payload);
        toast.success("Badge créé");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="tag-form-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-lg my-8" data-testid="tag-form">
        <div className="border-b border-zinc-900 p-4 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tight uppercase">{isEdit ? "Modifier le badge" : "Nouveau badge"}</div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nom">
            <input data-testid="input-name" required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="ex: Badge Marc Dubois"
              className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm" />
          </Field>
          <Field label="MAC ou UUID">
            <input data-testid="input-identifier" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF ou 550e8400-..."
              className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm" />
            <p className="text-[10px] font-mono text-zinc-500 mt-1">MAC (6 octets ":") ou UUID hex (8–128 chars).</p>
          </Field>

          <Field label="Type de badge">
            <div className="grid grid-cols-3 gap-2" data-testid="type-selector">
              {["driver", "passenger", "station"].map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const active = tagType === t;
                return (
                  <button key={t} type="button" onClick={() => setTagType(t)} data-testid={`type-${t}`}
                    className={`border border-zinc-900 p-2 text-xs font-bold uppercase tracking-widest inline-flex flex-col items-center gap-1 ${active ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
                    <Icon size={16} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-2">{TYPE_META[tagType]?.desc}</p>
          </Field>

          {tagType === "driver" && (
            <Field label="Chauffeur lié *">
              <select required data-testid="select-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Choisir un chauffeur —</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} · {d.email}</option>
                ))}
              </select>
            </Field>
          )}

          {tagType === "passenger" && (
            <Field label="Enfant lié *">
              <select required data-testid="select-child" value={childId} onChange={(e) => setChildId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Choisir un enfant —</option>
                {childrenList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.school ? ` · ${c.school}` : ""}</option>
                ))}
              </select>
            </Field>
          )}

          {tagType === "station" && (
            <Field label="Lieu / nom de borne *">
              <input required data-testid="input-station" value={stationLabel} onChange={(e) => setStationLabel(e.target.value)}
                placeholder="ex: Dépôt Morges - Hangar 2"
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm" />
            </Field>
          )}

          <Field label="Batterie (%)">
            <input data-testid="input-battery" type="number" min={0} max={100} value={battery} onChange={(e) => setBattery(e.target.value)}
              className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm" />
          </Field>
        </div>
        <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
            Annuler
          </button>
          <button type="submit" disabled={saving} data-testid="submit-tag"
            className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin inline" /> : null} {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
